import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import {
    Phone,
    Mail,
    MessageCircle,
    ChevronRight,
    Clock,
    CheckCircle2,
    AlertCircle,
    ArrowLeft,
    Send,
    Package,
    Truck,
    CreditCard,
    RefreshCw,
    Box,
    User as UserIcon,
    HelpCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ticketsAPI } from '../lib/api';

interface Ticket {
    _id: string;
    subject: string;
    description: string;
    category: string;
    status: string;
    priority: string;
    createdAt: string;
    userId?: {
        fullName: string;
        email: string;
    };
    messages: Array<{
        senderId: string;
        message: string;
        timestamp: string;
    }>;
}

const HelpSupport = () => {
    const navigate = useNavigate();
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('faqs');
    const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
    const [newMessage, setNewMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        subject: '',
        category: 'order',
        priority: 'medium',
        description: ''
    });

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }
        fetchTickets();
    }, [navigate]);

    const fetchTickets = async () => {
        try {
            const response = await ticketsAPI.getAll();
            setTickets(response.data);
        } catch (error) {
            console.error('Error fetching tickets:', error);
            toast.error('Failed to load tickets');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateTicket = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.subject.trim() || !formData.description.trim()) {
            toast.error('Please fill all required fields');
            return;
        }

        setSubmitting(true);
        try {
            await ticketsAPI.create(formData);
            toast.success('Ticket raised successfully');
            setFormData({
                subject: '',
                category: 'order',
                priority: 'medium',
                description: ''
            });
            fetchTickets();
            setActiveTab('tickets');
        } catch (error) {
            toast.error('Failed to raise ticket');
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newMessage.trim() || !selectedTicket) return;

        try {
            const response = await ticketsAPI.addMessage(selectedTicket._id, newMessage);
            setSelectedTicket(response.data);
            setNewMessage('');
            fetchTickets();
        } catch (error) {
            toast.error('Failed to send message');
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'open': return 'bg-blue-100 text-blue-600';
            case 'in_progress': return 'bg-orange-100 text-orange-600';
            case 'resolved': return 'bg-green-100 text-green-600';
            case 'closed': return 'bg-gray-100 text-gray-600';
            default: return 'bg-gray-100 text-gray-600';
        }
    };

    const faqs = [
        { q: 'How do I track my order?', a: 'You can track your order in the "Orders" section of your dashboard. Once an order is placed, you will see real-time updates on its status.' },
        { q: 'Can I cancel my subscription?', a: 'Yes, you can cancel or pause your subscription at any time from the "Subscriptions" page. Changes will take effect from the next billing cycle.' },
        { q: 'What are the delivery timings?', a: 'We offer multiple delivery slots starting from 6:00 AM to 9:00 PM. You can choose your preferred slot during checkout or in your subscription settings.' },
        { q: 'How do I add money to my wallet?', a: 'Go to the "Wallet" section and click on "Add Money". You can use UPI, Credit/Debit cards, or Net Banking.' },
    ];

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#10b981]"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="max-w-6xl mx-auto">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-gray-500 hover:text-gray-800 mb-4 transition-colors"
                >
                    <ArrowLeft size={18} />
                    <span>Back</span>
                </button>

                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-800">Help & Support</h1>
                    <p className="text-gray-500">Get help with your orders, subscriptions, and more</p>
                </div>

                {/* Contact Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
                        <div className="w-12 h-12 bg-green-50 text-[#10b981] rounded-xl flex items-center justify-center">
                            <Phone size={24} />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-gray-800">Call Us</p>
                            <p className="text-xs text-gray-500">1800-123-4567 (Toll Free)</p>
                        </div>
                    </div>
                    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-50 text-blue-500 rounded-xl flex items-center justify-center">
                            <Mail size={24} />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-gray-800">Email Us</p>
                            <p className="text-xs text-gray-500">support@acentle.com</p>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            setActiveTab('new');
                        }}
                        className="bg-[#10b981] p-6 rounded-2xl text-white shadow-lg shadow-[#10b981]/20 flex items-center gap-4 hover:bg-[#059669] transition-all"
                    >
                        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                            <MessageCircle size={24} />
                        </div>
                        <div className="text-left">
                            <p className="text-sm font-bold">Raise a Ticket</p>
                            <p className="text-xs opacity-80">Get help in 2-4 hours</p>
                        </div>
                    </button>
                </div>

                {/* Tabs */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="flex border-b border-gray-100">
                        <button
                            onClick={() => {
                                setActiveTab('faqs');
                                setSelectedTicket(null);
                            }}
                            className={`px-8 py-4 font-bold text-sm transition-all relative ${activeTab === 'faqs' ? 'text-[#10b981]' : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            FAQs
                            {activeTab === 'faqs' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#10b981]"></div>}
                        </button>
                        <button
                            onClick={() => {
                                setActiveTab('tickets');
                                setSelectedTicket(null);
                            }}
                            className={`px-8 py-4 font-bold text-sm transition-all relative ${activeTab === 'tickets' ? 'text-[#10b981]' : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            My Tickets
                            {activeTab === 'tickets' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#10b981]"></div>}
                        </button>
                        <button
                            onClick={() => setActiveTab('new')}
                            className={`px-8 py-4 font-bold text-sm transition-all relative ${activeTab === 'new' ? 'text-[#10b981]' : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            + New Ticket
                            {activeTab === 'new' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#10b981]"></div>}
                        </button>
                    </div>

                    <div className="p-6 lg:p-8">
                        {/* FAQs Tab */}
                        {activeTab === 'faqs' && (
                            <div className="space-y-4 animate-fade-in">
                                {faqs.map((faq, i) => (
                                    <div key={i} className="p-4 bg-gray-50 rounded-2xl">
                                        <p className="font-bold text-gray-800 mb-2">{faq.q}</p>
                                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* My Tickets Tab */}
                        {activeTab === 'tickets' && !selectedTicket && (
                            <div className="space-y-4 animate-fade-in">
                                {tickets.length === 0 ? (
                                    <div className="text-center py-12">
                                        <AlertCircle className="mx-auto text-gray-300 mb-4" size={48} />
                                        <p className="text-gray-500">No tickets raised yet</p>
                                    </div>
                                ) : (
                                    tickets.map((ticket) => (
                                        <div
                                            key={ticket._id}
                                            onClick={() => setSelectedTicket(ticket)}
                                            className="p-4 border border-gray-100 rounded-2xl hover:shadow-md transition-all cursor-pointer flex items-center justify-between"
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${getStatusColor(ticket.status)}`}>
                                                    {ticket.status === 'resolved' ? <CheckCircle2 size={20} /> : <Clock size={20} />}
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-gray-800">{ticket.subject}</h3>
                                                    <p className="text-xs text-gray-500">
                                                        #{ticket._id.substring(ticket._id.length - 6).toUpperCase()} • {new Date(ticket.createdAt).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${getStatusColor(ticket.status)}`}>
                                                    {ticket.status.replace('_', ' ')}
                                                </span>
                                                <ChevronRight size={18} className="text-gray-400" />
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}

                        {/* Ticket Detail View */}
                        {activeTab === 'tickets' && selectedTicket && (
                            <div className="animate-fade-in">
                                <button
                                    onClick={() => setSelectedTicket(null)}
                                    className="flex items-center gap-2 text-sm text-[#10b981] font-bold mb-6 hover:underline"
                                >
                                    <ArrowLeft size={16} />
                                    Back to Tickets
                                </button>

                                <div className="bg-gray-50 rounded-2xl p-6 mb-8">
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <h2 className="text-xl font-bold text-gray-800">{selectedTicket.subject}</h2>
                                            <p className="text-sm text-gray-500 mt-1">
                                                Category: <span className="capitalize">{selectedTicket.category}</span> •
                                                Priority: <span className="capitalize">{selectedTicket.priority}</span>
                                            </p>
                                        </div>
                                        <span className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${getStatusColor(selectedTicket.status)}`}>
                                            {selectedTicket.status.replace('_', ' ')}
                                        </span>
                                    </div>
                                    <p className="text-gray-700 leading-relaxed">{selectedTicket.description}</p>
                                </div>

                                <div className="space-y-6 mb-8">
                                    <h3 className="font-bold text-gray-800 flex items-center gap-2">
                                        <MessageCircle size={18} />
                                        Conversation
                                    </h3>
                                    <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                                        {selectedTicket.messages.map((msg: any, i: number) => {
                                            return (
                                                <div key={i} className={`flex ${i % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
                                                    <div className={`max-w-[80%] p-4 rounded-2xl ${i % 2 === 0 ? 'bg-gray-100 text-gray-800' : 'bg-[#10b981] text-white'
                                                        }`}>
                                                        <p className="text-sm">{msg.message}</p>
                                                        <p className={`text-[10px] mt-1 opacity-60 ${i % 2 === 0 ? 'text-gray-500' : 'text-white'}`}>
                                                            {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                <form onSubmit={handleAddMessage} className="flex gap-3">
                                    <input
                                        type="text"
                                        value={newMessage}
                                        onChange={(e) => setNewMessage(e.target.value)}
                                        placeholder="Type your message here..."
                                        className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] outline-none"
                                    />
                                    <button
                                        type="submit"
                                        className="bg-[#10b981] text-white p-3 rounded-xl hover:bg-[#059669] transition-all"
                                    >
                                        <Send size={20} />
                                    </button>
                                </form>
                            </div>
                        )}

                        {/* New Ticket Tab */}
                        {activeTab === 'new' && (
                            <div className="animate-fade-in">
                                <h2 className="text-xl font-bold text-gray-800 mb-6">Create Support Ticket</h2>
                                <form onSubmit={handleCreateTicket} className="space-y-8">
                                    {/* Category Selection */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-4">Category *</label>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            {[
                                                { id: 'order', label: 'Order', icon: Package },
                                                { id: 'delivery', label: 'Delivery', icon: Truck },
                                                { id: 'payment', label: 'Payment', icon: CreditCard },
                                                { id: 'subscription', label: 'Subscription', icon: RefreshCw },
                                                { id: 'product', label: 'Product', icon: Box },
                                                { id: 'account', label: 'Account', icon: UserIcon },
                                                { id: 'other', label: 'Other', icon: HelpCircle },
                                            ].map((cat) => {
                                                const Icon = cat.icon;
                                                const isSelected = formData.category === cat.id;
                                                return (
                                                    <button
                                                        key={cat.id}
                                                        type="button"
                                                        onClick={() => setFormData({ ...formData, category: cat.id })}
                                                        className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all gap-2 ${isSelected
                                                            ? 'border-[#10b981] bg-[#10b981]/5 text-[#10b981]'
                                                            : 'border-gray-100 hover:border-gray-200 text-gray-600'
                                                            }`}
                                                    >
                                                        <Icon size={24} />
                                                        <span className="text-sm font-medium">{cat.label}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Subject */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.subject}
                                            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                            placeholder="Brief description of your issue"
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] outline-none"
                                        />
                                    </div>

                                    {/* Description */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                                        <textarea
                                            required
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            placeholder="Please describe your issue in detail..."
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] outline-none h-40 resize-none"
                                        />
                                    </div>

                                    {/* Priority */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-4">Priority</label>
                                        <div className="flex flex-wrap gap-3">
                                            {['low', 'medium', 'high', 'urgent'].map((p) => (
                                                <button
                                                    key={p}
                                                    type="button"
                                                    onClick={() => setFormData({ ...formData, priority: p })}
                                                    className={`px-6 py-2.5 rounded-lg font-medium capitalize transition-all ${formData.priority === p
                                                        ? 'bg-[#1e3a5f] text-white shadow-md'
                                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                        }`}
                                                >
                                                    {p}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Submit Button */}
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="w-full bg-[#10b981] text-white py-4 rounded-xl font-bold hover:bg-[#059669] transition-all shadow-lg shadow-[#10b981]/20 disabled:opacity-50 flex items-center justify-center gap-2"
                                    >
                                        <Send size={20} />
                                        {submitting ? 'Raising Ticket...' : 'Submit Ticket'}
                                    </button>
                                </form>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default HelpSupport;
